<template>
  <div class="feed-page">
    <!-- Header -->
    <div class="feed-header">
      <h2>SharePay</h2>
      <p>发现优质资源</p>
    </div>

    <!-- Not logged in banner -->
    <div v-if="!isLoggedIn" class="login-banner">
      <div class="login-banner-inner">
        <van-icon name="user-o" size="20" color="#6366f1" />
        <span>登录后关注博主，发现更多资源</span>
        <van-button
          size="small" round
          color="linear-gradient(135deg, #6366f1, #8b5cf6)"
          @click="$router.push('/login')"
        >
          登录
        </van-button>
      </div>
    </div>

    <!-- Empty: no follows -->
    <div v-else-if="!loading && list.length === 0 && !finished" class="empty-follows">
      <van-empty image="search" description="你还没有关注任何博主">
        <p class="empty-tip">通过博主分享的链接进入，即可关注博主</p>
      </van-empty>
    </div>

    <!-- Feed list -->
    <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
      <van-list
        v-model="loading"
        :finished="finished"
        finished-text="— 没有更多了 —"
        @load="onLoad"
      >
        <div v-for="item in list" :key="item.id" class="feed-card">
          <!-- Blogger info row -->
          <div class="feed-blogger" @click="$router.push('/blogger/' + item.bloggerId)">
            <div class="blogger-avatar">
              <img v-if="item.bloggerAvatar" :src="item.bloggerAvatar" alt="" />
              <van-icon v-else name="user-o" size="22" color="#a5b4fc" />
            </div>
            <div class="blogger-meta">
              <span class="blogger-name">{{ item.bloggerNickname || item.bloggerName }}</span>
              <span class="feed-time">{{ timeAgo(item.createdAt) }}</span>
            </div>
          </div>

          <!-- Content -->
          <div class="feed-content" @click="goDetail(item)">
            <h3 class="feed-title">{{ item.title }}</h3>
            <p class="feed-desc" v-if="item.description">{{ item.description }}</p>

            <!-- Cover image -->
            <div v-if="item.coverUrl" class="feed-cover" @click.stop="previewImage(item.coverUrl)">
              <img :src="item.coverUrl" alt="" />
            </div>
          </div>

          <!-- Actions row -->
          <div class="feed-actions">
            <div class="action-like" @click="onToggleLike(item)">
              <van-icon
                :name="item.isLiked ? 'good-job' : 'good-job-o'"
                :color="item.isLiked ? '#ef4444' : '#9ca3af'"
                size="18"
              />
              <span :class="{ liked: item.isLiked }">{{ item.likeCount || 0 }}</span>
            </div>

            <div class="action-right">
              <template v-if="!item.isPaid">
                <span class="tag-free" @click="goDetail(item)">免费</span>
              </template>
              <template v-else-if="item.isPurchased">
                <span class="tag-purchased" @click="goDetail(item)">
                  <van-icon name="success" size="12" /> 已购
                </span>
              </template>
              <template v-else>
                <span class="tag-price" @click="goDetail(item)">
                  ¥{{ item.price }}
                </span>
              </template>
            </div>
          </div>
        </div>
      </van-list>
    </van-pull-refresh>
  </div>
</template>

<script>
import { resourceApi } from '../api';
import { ImagePreview } from 'vant';

export default {
  data() {
    return {
      list: [], page: 1, size: 10, loading: false, finished: false, refreshing: false
    };
  },
  computed: {
    isLoggedIn() { return !!this.$store.state.token; }
  },
  methods: {
    async onLoad() {
      if (!this.isLoggedIn) {
        this.loading = false;
        this.finished = true;
        return;
      }
      const res = await resourceApi.getFeed({ page: this.page, size: this.size });
      const data = res.data;
      if (this.page === 1) this.list = data.list;
      else this.list = [...this.list, ...data.list];
      this.loading = false;
      if (this.list.length >= data.total) this.finished = true;
      this.page++;
    },
    async onRefresh() {
      this.page = 1; this.finished = false;
      this.loading = true;
      await this.onLoad();
      this.refreshing = false;
    },
    goDetail(item) {
      this.$router.push('/resource/' + item.id);
    },
    async onToggleLike(item) {
      if (!this.isLoggedIn) {
        this.$router.push('/login');
        return;
      }
      try {
        if (item.isLiked) {
          const res = await resourceApi.unlike(item.id);
          item.isLiked = false;
          item.likeCount = res.data.likeCount;
        } else {
          const res = await resourceApi.like(item.id);
          item.isLiked = true;
          item.likeCount = res.data.likeCount;
        }
      } catch (e) { /* ignore */ }
    },
    previewImage(url) {
      ImagePreview([url]);
    },
    timeAgo(dateStr) {
      if (!dateStr) return '';
      const now = Date.now();
      const diff = now - new Date(dateStr).getTime();
      const min = Math.floor(diff / 60000);
      if (min < 1) return '刚刚';
      if (min < 60) return min + '分钟前';
      const hour = Math.floor(min / 60);
      if (hour < 24) return hour + '小时前';
      const day = Math.floor(hour / 24);
      if (day < 30) return day + '天前';
      return Math.floor(day / 30) + '个月前';
    }
  }
};
</script>

<style scoped>
.feed-page {
  min-height: 100vh; background: #f5f7fa;
}
.feed-header {
  padding: 16px 20px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
}
.feed-header h2 {
  margin: 0; font-size: 22px; font-weight: 700;
}
.feed-header p {
  margin: 2px 0 0; font-size: 13px; opacity: 0.8;
}

.login-banner {
  margin: 12px 16px;
}
.login-banner-inner {
  display: flex; align-items: center; gap: 10px;
  background: #eef2ff; border-radius: 12px;
  padding: 12px 16px;
  font-size: 13px; color: #4f46e5;
}

.empty-follows { padding-top: 40px; }
.empty-tip {
  text-align: center; font-size: 12px; color: #9ca3af; margin-top: 4px;
}

/* Feed card */
.feed-card {
  margin: 10px 16px; background: #fff;
  border-radius: 14px; overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

/* Blogger row */
.feed-blogger {
  display: flex; align-items: center; gap: 10px;
  padding: 14px 14px 0; cursor: pointer;
}
.blogger-avatar {
  width: 40px; height: 40px; border-radius: 50%;
  background: #eef2ff; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.blogger-avatar img {
  width: 100%; height: 100%; object-fit: cover;
}
.blogger-meta {
  display: flex; flex-direction: column;
}
.blogger-name {
  font-size: 14px; font-weight: 600; color: #1f2937;
}
.feed-time {
  font-size: 11px; color: #cbd5e1; margin-top: 1px;
}

/* Content */
.feed-content {
  padding: 10px 14px 0; cursor: pointer;
}
.feed-title {
  font-size: 16px; font-weight: 700; color: #1f2937;
  margin: 0; line-height: 1.4;
}
.feed-desc {
  font-size: 14px; color: #6b7280; line-height: 1.5;
  margin: 6px 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 3; -webkit-box-orient: vertical;
  overflow: hidden;
}
.feed-cover {
  margin-top: 10px; border-radius: 10px;
  overflow: hidden; background: #f1f5f9;
  max-height: 320px;
}
.feed-cover img {
  width: 100%; display: block;
  max-height: 320px; object-fit: cover;
}

/* Actions */
.feed-actions {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 14px;
}
.action-like {
  display: flex; align-items: center; gap: 4px;
  cursor: pointer; padding: 4px 0;
}
.action-like span {
  font-size: 13px; color: #9ca3af;
}
.action-like span.liked {
  color: #ef4444; font-weight: 600;
}
.tag-free {
  display: inline-flex; align-items: center;
  padding: 4px 12px; border-radius: 12px;
  background: #ecfdf5; color: #10b981;
  font-size: 12px; font-weight: 600; cursor: pointer;
}
.tag-purchased {
  display: inline-flex; align-items: center; gap: 3px;
  padding: 4px 12px; border-radius: 12px;
  background: #eef2ff; color: #6366f1;
  font-size: 12px; font-weight: 600; cursor: pointer;
}
.tag-price {
  display: inline-flex; align-items: center;
  padding: 4px 12px; border-radius: 12px;
  background: #fef2f2; color: #ef4444;
  font-size: 13px; font-weight: 700; cursor: pointer;
}
</style>
